import { JobStatus } from '@seek/hirer-job-information';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Box,
  Secondary,
  Inline,
  Strong,
  TextLinkButton,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import type { FC } from 'react';

import Skeleton from '../../common/Skeleton';

import translations from './.vocab/';

import * as styles from './styles.css';

interface Props {
  status: JobStatus;
  numberOfDaysRemaining: number | null;
  billableApplicants?:
    | {
        success: true;
        billableApplicantsCount: number;
        maxBillableApplicants: number;
      }
    | {
        success: false;
      };
  internalReference?: string;
  bucketStatsTotalCount: number;
  openRelevantApplicationDialog: () => void;
}

const JobStatDetail: FC<Props> = ({
  status,
  numberOfDaysRemaining,
  billableApplicants,
  internalReference,
  bucketStatsTotalCount,
  openRelevantApplicationDialog,
}) => {
  const { t } = useTranslations(translations);

  const getJobExpiryItem = () => {
    if (numberOfDaysRemaining === null) {
      return undefined;
    }
    if (JobStatus.ACTIVE === status) {
      if (numberOfDaysRemaining === 0) {
        return <Text>{t('Expires today')}</Text>;
      }
      return (
        <Text>
          {t('Days left', {
            statNumber: numberOfDaysRemaining,
            Strong: (children) => <Strong>{children}</Strong>,
          })}
        </Text>
      );
    }
    return undefined;
  };

  return (
    <Layout>
      {getJobExpiryItem()}
      <Text>
        {t('Total applications', {
          statNumber: bucketStatsTotalCount, // FIXME: We shouldn't have undefined bucketStatsTotalCount
          Strong: (children) => <Strong>{children}</Strong>,
        })}
      </Text>

      {internalReference ? (
        <Text weight="medium">
          {t('Internal Reference', {
            Reference: () => <Secondary>{internalReference}</Secondary>,
          })}
        </Text>
      ) : null}

      {billableApplicants && (
        <Inline alignY="center" space="xsmall">
          {billableApplicants.success ? (
            <Box
              cursor="pointer"
              onClick={() => {
                openRelevantApplicationDialog();
              }}
            >
              <Inline space="xsmall" alignY="center">
                <Box
                  background="promoteLight"
                  borderRadius="standard"
                  paddingX="xsmall"
                  className={styles.inlineTextBadge}
                >
                  {billableApplicants.billableApplicantsCount}/
                  {billableApplicants.maxBillableApplicants}
                </Box>
                <Text weight="medium">
                  <TextLinkButton weight="weak">
                    <Strong>{t('Relevant applications')}</Strong>
                  </TextLinkButton>
                </Text>
              </Inline>
            </Box>
          ) : (
            <Text weight="medium">
              <TextLinkButton
                weight="weak"
                onClick={() => {
                  openRelevantApplicationDialog();
                }}
              >
                <Strong>{t('Relevant applications')}</Strong>
              </TextLinkButton>
            </Text>
          )}
        </Inline>
      )}
    </Layout>
  );
};

const Layout: FC<{
  children: Array<JSX.Element | undefined | null | false>;
}> = ({ children }) => (
  <Box data-testid="job-stats-summary">
    <Inline space="small" alignY="center">
      {children}
    </Inline>
  </Box>
);

export const JobStatDetailSkeleton: FC = () => {
  const { t } = useTranslations(translations);

  return (
    <Layout>
      <Text>
        {t('Days left', {
          statNumber: -1, // FIXME: We shouldn't have pass in -1 as loading state
          Strong: (children) => (
            <Skeleton loading={true} numberOfCharacters={8}>
              {children}
            </Skeleton>
          ),
        })}
      </Text>
      <Text>
        {t('Total applications', {
          statNumber: -1, // FIXME: We shouldn't have pass in -1 as loading state
          Strong: (children) => (
            <Skeleton loading={true} numberOfCharacters={8}>
              {children}
            </Skeleton>
          ),
        })}
      </Text>
    </Layout>
  );
};

export default observer(JobStatDetail);
